
/* eslint-disable @typescript-eslint/camelcase */
import { Options, prop, Vue } from "vue-class-component";
import DatePicker from "vue2-datepicker";
import DatePickerV2 from "@/app/ui/components/date-picker-v2/index.vue";
import moment from "moment";
import "vue2-datepicker/index.css";
import {
  formatLocalizedDate,
  parseFuzzyLocalizedDate
} from "@/app/infrastructures/misc/common-library/AutocorrectDate";

moment.locale("id");

class Props {
  title = prop<string>({ default: "Tanggal dan jam" });
  minDate = prop<Date>({ default: () => new Date() });
  modelValue = prop<Date | null>({ default: null });
  isVisible = prop<boolean>({
    default: false,
    type: Boolean,
    required: true
  });
  validateTodayMinHour = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  name: "DateTimeInput",
  components: {
    DatePicker,
    DatePickerV2
  },
  watch: {
    isVisible(val: boolean) {
      if (val) {
        this.init();
      }
    },
    modelValue() {
      if (this.isVisible) {
        this.init();
      }
    }
  }
})
export default class DateTimeInput extends Vue.with(Props) {
  draftValue: Date | null = null;
  draftDateText = "";
  draftTimeText = "00:00";

  mounted() {
    if (this.isVisible) {
      this.init();
    }
  }

  get visible() {
    return this.isVisible;
  }

  set visible(val: boolean) {
    this.$emit("update:isVisible", val);
  }

  get normalizedMinDate() {
    return new Date(this.minDate);
  }

  get draftCalendarDate() {
    return this.draftValue ? new Date(this.draftValue) : this.normalizedMinDate;
  }

  get draftTimeDate() {
    return new Date(
      this.draftCalendarDate.getFullYear(),
      this.draftCalendarDate.getMonth(),
      this.draftCalendarDate.getDate(),
      this.draftCalendarDate.getHours(),
      this.draftCalendarDate.getMinutes()
    );
  }

  onClose() {
    this.visible = false;
  }

  onSave() {
    const value = this.clampToMinDate(
      this.draftValue || this.normalizedMinDate
    );
    const nextValue = new Date(value);

    this.$emit("update:modelValue", nextValue);
    this.$emit("save", nextValue);
    this.visible = false;
  }

  init() {
    const source = this.modelValue
      ? new Date(this.modelValue)
      : this.normalizedMinDate;
    this.applyDraftValue(this.clampToMinDate(source));
  }

  onInputDateText(event: Event) {
    this.draftDateText = (event.target as HTMLInputElement).value;
  }

  onBlurDate(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    const rawValue = inputEl.value;
    const parsedDate = parseFuzzyLocalizedDate(rawValue, "id");

    if (!parsedDate) {
      this.revertInput(inputEl, this.draftCalendarDate);
      return;
    }

    const normalizedDate = this.normalizeDate(parsedDate);
    const nextValue = this.mergeDateAndTime(normalizedDate, this.draftTimeText);

    this.applyDraftValue(this.clampToMinDate(nextValue));
    this.revertInput(inputEl, normalizedDate);
    this.onValidateTime();
  }

  onTimeSelected(value: Date) {
    const nextTime = moment(value).format("HH:mm");
    const nextValue = this.mergeDateAndTime(this.draftCalendarDate, nextTime);
    this.applyDraftValue(this.clampToMinDate(nextValue));
  }

  selectCalendarDate(date: Date) {
    const nextValue = this.mergeDateAndTime(date, this.draftTimeText);
    this.applyDraftValue(this.clampToMinDate(nextValue));
  }

  isDateDisabled(date: Date) {
    return moment(date)
      .startOf("day")
      .isBefore(moment(this.normalizedMinDate).startOf("day"));
  }

  private isSameDayAsMinDate(date: Date) {
    return moment(date).isSame(this.normalizedMinDate, "day");
  }

  private normalizeDate(parsedDate: Date): Date {
    return moment(parsedDate)
      .startOf("day")
      .toDate();
  }

  private revertInput(inputEl: HTMLInputElement, date: Date) {
    inputEl.value = formatLocalizedDate(date, "id");
  }

  private onValidateTime() {
    if (
      this.isSameDayAsMinDate(this.draftCalendarDate) &&
      moment(this.draftCalendarDate).isBefore(this.normalizedMinDate)
    ) {
      this.applyDraftValue(new Date(this.normalizedMinDate));
    }
  }

  private applyDraftValue(value: Date) {
    const nextValue = new Date(value);
    this.draftValue = nextValue;
    this.draftDateText = formatLocalizedDate(nextValue, "id");
    this.draftTimeText = moment(nextValue).format("HH:mm");
  }

  private mergeDateAndTime(date: Date, time: string) {
    const [hours, minutes] = time.split(":").map((value) => Number(value) || 0);

    return moment(date)
      .hours(hours)
      .minutes(minutes)
      .seconds(0)
      .milliseconds(0)
      .toDate();
  }

  private clampToMinDate(date: Date) {
    // if validateTodayMinHour is true and if selected date is today, then the min hour is +1 hour from current time
    if (this.validateTodayMinHour && moment(date).isSame(this.normalizedMinDate, "day")) {
      const minHourDate = moment().add(1, "hour").toDate();
      return moment(date).isBefore(minHourDate)
        ? new Date(minHourDate)
        : date;
    }
    return moment(date).isBefore(this.normalizedMinDate)
      ? new Date(this.normalizedMinDate)
      : date;
  }
}
